<template>
  <utd-modal-dialog ref="magMutualRef"
                    :modal-classes="'mag-mutual-modal'"
                    :dialog-classes="'ds1-default-dialog'"
                    :close-on-escape="true"
                    @closed="closeDialog">
    <template #header>
      <div class="header-container">
        <img src="/search/mag-mutual-logo.svg" class="mag-mutual-logo" alt="mag-mutual-logo">
        <span class="header-text">
          {{ C_MAG_MUTUAL.INFO }}
        </span>
      </div>
    </template>
    <template v-slot>
      <div class="disclaimer-text">
        <strong>{{ C_MAG_MUTUAL.DISCLAIMER_BOLDED }}</strong>
        {{ C_MAG_MUTUAL.DISCLAIMER }}
      </div>
      <div class="mag-mutual-modal-content">
        <div class="mag-mutual-content-header">
          {{ C_MAG_MUTUAL.MITIGATE_YOUR_RISK }}
        </div>
        <div class="columns">
          <div class="column left-column">
            <slot name="risk-factor-header" />
            <slot name="risk-factor-causes" />
          </div>
          <div class="column">
            <slot name="risk-factor-paragraphs" />
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <a :href="magMutualLink" 
         class="footer-link" 
         target="_blank"
         aria-label="Link to MagMutual Content"
         @click="logUiClickEvent('InsituteLink', magMutualLink)">
        <div>Go to <b>MagMutual</b> for more information</div>
        <span class="wkce-icon-link-external" />
      </a>
    </template>
  </utd-modal-dialog>
</template>

<script setup>

import UtdModalDialog from '_acaSrc/components/shared/stdlib/UtdModalDialog.vue';
import { ref, nextTick } from 'vue';
import { C_MAG_MUTUAL } from '_acaSrc/utility/constants';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';

const magMutualRef = ref(null);
const triggerElement = ref(null);
const magMutualLink = C_MAG_MUTUAL.RISK_REPORT_LINK;
const emits = defineEmits([ 'closed' ]);

const open = (event) => {
    if (event?.currentTarget) {
        triggerElement.value = event.currentTarget;
    }
    magMutualRef.value.open();
};

const closeDialog = () => {
    emits('closed');
    logUiClickEvent('closeModal');
    nextTick(() => {
        if (triggerElement.value) {
            triggerElement.value.setAttribute('tabindex', 0);
            triggerElement.value.focus();
        }
    });
};

defineExpose({
    open,
    closeDialog
});

function logUiClickEvent(element, url) {
    logUiClickEventHelper({
        uiElementName: `MagMutual_${element}`,
        targetUrl: url
    });
}

</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.mag-mutual-modal {
  .utd-modal-content .utd-dialog-frame {
      width: 320px;
  }

  .utd-dialog-header {
    .ds1-pl-2();
    .ds1-pv-0();
    background-color: @WKCE-GRAY;
    display: flex;
    height: fit-content;
    align-items: center;
    border-bottom: 3px solid @WKCE-GREEN;
    box-sizing: border-box;

    .wkce-icon-button {
      .ds1-pa-0();
      .ds1-pb-1();
      position: absolute;
      top: 28px;
      right: 16px;
    }

    .header-container {
      .ds1-mv-2();
      display: flex;
      flex-direction: column;
      gap: 12px;

      .mag-mutual-logo {
        width: 145px;
        height: 22px;
      }

      .header-text {
        .ds1-pr-0();
        font-size: 11px;
      }
    }

    .header-text{
      .ds1-pr-4();
      color: @WKCE-WHITE;
      font-size: 11px;
      font-style: italic;
      font-weight: 400;
      line-height: 14px;
    }
  }

  .utd-dialog-footer {
    .ds1-pa-0();
    .ds1-pr-3();
    height: 60px;
    background-color: @DS2-UTD-FOOTER-BG-COLOR;

    > a {
      color: @WKCE-GRAY-SHADE1;
      font-size: 12px;
      display: flex;
      align-items: center;
    }

    .wkce-icon-link-external {
      .ds1-pl-3();
      margin-left: auto;
    }
  }

  .disclaimer-text {
    .ds1-pa-2();
    align-items: center;
    gap: 10px;
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
    background-color: @DS2-UTD-FOOTER-BG-COLOR;
    color: @WKCE-ORANGE-SHADE4;
  }

  .mag-mutual-modal-content {
    .ds1-pa-2();
    display: flex;
    height: 400px;
    flex-direction: column;

    .mag-mutual-content-header {
      color: @WKCE-GRAY;
      font-weight: 700;
      font-size: 20px;
    }
  }

  .columns {
    .column {
      .ds1-pt-2();
    }

    .left-column {
      border-bottom: 1px solid @UTD-LEGACY-WHITE-TINT5;
    }
  }

  .footer-link:hover {
    color: @WKCE-GRAY-SHADE1;
  }
}

@media screen and (min-width: 768px) {
  .mag-mutual-modal {
    .utd-modal-content .utd-dialog-frame {
      width: 60vw;
    }

    .utd-dialog-header {
      .ds1-pv-2();
      padding-right: 92px;
      height: 56px;

      .wkce-icon-button {
        .ds1-pt-1();
      }

      .header-container {
        flex-direction: row;
        gap: 16px;
        align-items: center;
      }
    }

    .disclaimer-text {
      box-sizing: border-box;
    }

    .columns {
      display: flex;
      flex-direction: row;
      gap: 24px;
      
      .column {
        .ds1-pt-0();
        flex: 1;
      }

      .left-column {
        .ds1-pl-3();
        .ds1-pr-3();
        .ds1-pt-2();
        max-width: 32%;
        border-right: 1px solid @UTD-LEGACY-WHITE-TINT5;
        border-bottom: 0;
      }
    }

    .mag-mutual-modal-content {
      .mag-mutual-content-header {
          .ds1-pb-2();
          font-size: 20px;
      }
    }
    
    .utd-dialog-footer {
      > a {
        font-size: 14px;
      }
    }
  }
}


@media screen and (min-width: 1280px) {
  .mag-mutual-modal {
    .utd-modal-content .utd-dialog-frame {
      width: 840px;
    }
  }
}
</style>